/**
 * Pinterest API for Conversions Adapter
 *
 * Adapts standardized event payloads into Pinterest Conversions API format.
 * Handles both browser pixel and server-to-server (S2S) data structures.
 *
 * @see https://developers.pinterest.com/docs/conversions/best/
 * @see https://developers.pinterest.com/docs/api/v5/#operation/events/create
 * @see https://developers.pinterest.com/docs/conversions/event/
 */

(function (pmw) {
	"use strict";

	/**
	 * Pinterest Event Adapter
	 */
	class PinterestAdapter {
		/**
		 * Adapt event data to Pinterest format
		 *
		 * @param {object} eventData - Standardized event data
		 * @param {string} eventName - Event name (e.g., "add_to_cart")
		 * @param {object} context - Additional context (user, page, etc.)
		 * @returns {object|null} Pinterest-formatted event data or null if event not supported
		 */
		adapt(eventData, eventName, context) {
			// Only process if Pinterest pixel is loaded
			if (!pmwDataLayer?.pixels?.pinterest?.loaded) {
				return null;
			}

			// Map PMW's internal event names to Pinterest's standard event names.
			// Pinterest documents the Conversions API names in snake_case, which is
			// what we emit here. The browser tag uses its own spelling of the same
			// standard events and is mapped separately in the event listeners.
			// https://developers.pinterest.com/docs/track-conversions/track-conversions-in-the-api/
			const eventMap = {
				page_view      : () => this.adaptPageView(eventData),
				add_to_cart    : () => this.adaptAddToCart(eventData),
				view_item      : () => this.adaptViewContent(eventData),
				begin_checkout : () => this.adaptInitiateCheckout(eventData),
				purchase       : () => this.adaptPurchase(eventData),
				search         : () => this.adaptSearch(eventData),
				view_category  : () => this.adaptViewCategory(eventData),
				add_to_wishlist: () => this.adaptAddToWishlist(eventData),
				signup         : () => this.adaptSignup(eventData),
				lead           : () => this.adaptLead(eventData),
			};

			if (eventMap[eventName]) {
				return eventMap[eventName]();
			}

			return null;
		}

		/**
		 * Get base Pinterest event data structure
		 *
		 * The browser tag only reads the event_id off this. The user data the
		 * API for Conversions needs comes from functions_premium.js, which the
		 * free build does not ship, so the base is built without it there.
		 *
		 * @returns {object} Base event data with event_id, user, and page
		 */
		getBaseData() {

			if (typeof pmw.getPinterestS2SBaseData === "function") {
				return pmw.getPinterestS2SBaseData();
			}

			const data = {
				action_source   : "web",
				event_time      : Math.round(Date.now() / 1000),
				event_id        : pmw.getRandomEventId(),
				event_source_url: window.location.href,
			};

			const language = navigator.language || navigator.userLanguage;
			if (language) {
				data.language = language;
			}

			return data;
		}

		/**
		 * Adapt AddToCart event
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} Pinterest addtocart event
		 */
		adaptAddToCart(eventData) {
			const data      = this.getBaseData();
			data.event_name = "add_to_cart";

			if (eventData.product) {
				data.custom_data = pmw.pinterestGetProductDataForCapiEvent(eventData.product);
			}

			return data;
		}

		/**
		 * Adapt ViewContent event (single product view)
		 *
		 * Pinterest's page_visit covers any page. A product view is view_content.
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} Pinterest view_content event
		 */
		adaptViewContent(eventData) {
			const data      = this.getBaseData();
			data.event_name = "view_content";

			if (eventData.product) {
				data.custom_data = pmw.pinterestGetProductDataForCapiEvent(eventData.product);
			}

			return data;
		}

		/**
		 * Adapt InitiateCheckout event
		 *
		 * Pinterest defines checkout as a completed order, so a checkout that has
		 * only been started belongs on initiate_checkout instead.
		 *
		 * begin_checkout dispatches with an empty eventData, so the cart is read
		 * from the data layer, the same way the other adapters do it. Reading
		 * eventData here left every event without a value.
		 *
		 * @param {object} eventData - Unused, kept for signature parity
		 * @returns {object} Pinterest initiate_checkout event
		 */
		adaptInitiateCheckout(eventData) {
			const data      = this.getBaseData();
			data.event_name = "initiate_checkout";

			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {

				const cartItems = Object.values(pmwDataLayer.cart);

				data.custom_data = {
					value      : String(pmw.getCartValue()),
					currency   : pmwDataLayer?.shop?.currency,
					content_ids: cartItems.map(item => pmw.getProductIdForSpecificPixel(item, "pinterest")).filter(id => null !== id),
					num_items  : cartItems.reduce((total, item) => total + item.quantity, 0),
				};
			}

			return data;
		}

		/**
		 * Adapt Purchase event
		 *
		 * @param {object} eventData - Event data with transaction information
		 * @returns {object} Pinterest checkout event (Pinterest uses checkout for completed purchases)
		 */
		adaptPurchase(eventData) {
			const data = this.getBaseData();
			// Use order ID for purchase event to match server-side event ID
			if (pmwDataLayer?.order?.id) {
				data.event_id = "pmw_" + pmwDataLayer.order.id.toString();
			}
			data.event_name = "checkout";

			if (eventData.transaction) {
				const transaction = eventData.transaction;
				data.custom_data  = {
					value   : transaction.value || 0,
					currency: transaction.currency || pmwDataLayer?.shop?.currency,
					order_id: transaction.transaction_id || transaction.order_id,
				};

				if (transaction.items && transaction.items.length > 0) {
					data.custom_data.content_ids = transaction.items.map(item => pmw.getProductIdForSpecificPixel(item, "pinterest")).filter(id => null !== id);
					data.custom_data.num_items   = transaction.items.reduce((total, item) => total + item.quantity, 0);
				}
			}

			return data;
		}

		/**
		 * Adapt Search event
		 *
		 * @param {object} eventData - Event data with search query
		 * @returns {object} Pinterest search event
		 */
		adaptSearch(eventData) {
			const data      = this.getBaseData();
			data.event_name = "search";

			data.custom_data = {
				search_string: pmw.getSearchTermFromUrl(),
			};

			return data;
		}

		/**
		 * Adapt Signup event
		 *
		 * @param {object} eventData - Event data with user information
		 * @returns {object} Pinterest signup event
		 */
		adaptSignup(eventData) {
			const data      = this.getBaseData();
			data.event_name = "signup";

			return data;
		}

		/**
		 * Adapt Lead event
		 *
		 * @param {object} eventData - Event data with lead information
		 * @returns {object} Pinterest lead event
		 */
		adaptLead(eventData) {
			const data      = this.getBaseData();
			data.event_name = "lead";

			if (eventData.value) {
				data.custom_data = {
					value   : eventData.value,
					currency: eventData.currency || pmwDataLayer?.shop?.currency,
				};
			}

			return data;
		}

		/**
		 * Adapt PageView event
		 *
		 * Note: Pinterest doesn't have a dedicated PageView event for S2S.
		 * Return null as this event is not supported for server-side tracking.
		 *
		 * @param {object} eventData - Event data
		 * @returns {null} Pinterest doesn't support page view S2S
		 */
		adaptPageView(eventData) {
			// Pinterest Conversions API doesn't support PageView event
			return null;
		}

		/**
		 * Adapt ViewCategory event
		 *
		 * @param {object} eventData - Event data
		 * @returns {object} Pinterest viewcategory event
		 */
		adaptViewCategory(eventData) {
			const data      = this.getBaseData();
			data.event_name = "view_category";

			return data;
		}

		/**
		 * Adapt AddToWishlist event
		 *
		 * add_to_wishlist is one of Pinterest's standard events.
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} Pinterest add_to_wishlist event
		 */
		adaptAddToWishlist(eventData) {
			const data      = this.getBaseData();
			data.event_name = "add_to_wishlist";

			if (eventData.product) {
				data.custom_data = pmw.pinterestGetProductDataForCapiEvent(eventData.product);
			}

			return data;
		}
	}

	// Register the adapter with S2S capabilities
	const adapter = new PinterestAdapter();
	pmw.registerAdapter("pinterest", adapter, {
		supportsS2S               : true,
		supportsEventDeduplication: true,
		supportsPageViewS2S       : false,
		isActive                  : () => pmwDataLayer?.pixels?.pinterest?.loaded === true && pmw.consent.categories.get().marketing === true,
		// Matches the other adapters: the pixel ID is the marker that Pinterest is
		// set up at all. ad_account_id was checked here before, but it is never
		// written to the data layer, so this always evaluated to false and the
		// always_send_s2s fallback never engaged for Pinterest.
		isConfigured              : () => !!pmwDataLayer?.pixels?.pinterest?.pixel_id,
	});

})(window.pmw = window.pmw || {});
